import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import { useSelector } from "react-redux";
import { selectIsAuthenticated } from "../auth/authSelectors";

/* -------------------- CONFIG & UTILS -------------------- */
const API_URL = "https://aadhavacademy.com/api/auth/tournaments.php";
const REGISTRATION_BASE_PATH = "/tournament-registration?id=";

const formatDate = (d) =>
  new Date(d).toLocaleDateString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const defaultImage = "https://via.placeholder.com/800x480/eceff1/90a4ae?text=Event+Image";
const getRegistrationPath = (id) => id ? REGISTRATION_BASE_PATH + id : '/';
const hasImage = (img) => img && img.trim() !== "";

/* -------------------- DATA HOOK -------------------- */
const useTournamentData = () => {
  const [tournaments, setTournaments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadTournaments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(API_URL);
      if (!response.ok) throw new Error("Network error");
      const data = await response.json();
      if (data.status !== "success" || !data.data) throw new Error("Invalid response");

      setTournaments(data.data);
      setCategories([
        { id: 0, name: "All" },
        ...(data.categories || []).map((c) => ({ id: c.id, name: c.name })),
      ]);
    } catch (err) {
      setError("Unable to load tournaments.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTournaments(); }, [loadTournaments]);
  return { tournaments, categories, loading, error };
};

/* -------------------- TOURNAMENT MODAL -------------------- */
function TournamentModal({ tournament, onClose, navigate, isAuthenticated }) {
  if (!tournament) return null;

  return (
    <div className="tp-modal-overlay" onClick={onClose}>
      <div className="tp-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="tp-modal-close-btn" onClick={onClose} aria-label="Close">&times;</button>
        
        <h2 className="tp-modal-header-tag">Tournament Details</h2>
        <h3 className="tp-modal-name">{tournament.name}</h3>

        <div className="tp-modal-scroll-area">
          <div className="tp-modal-media">
            <div className="tp-modal-media">
              <img src={tournament.prospectus_image || defaultImage} alt={tournament.name} />
              {hasImage(tournament.prospectus_back_image) && (
                <img src={tournament.prospectus_back_image} alt={`${tournament.name} back`} />
              )}
            </div>
          </div>
        </div>

        <div className="tp-modal-actions">
            <button className="tp-btn-card tp-btn-secondary" onClick={onClose}>Close</button>
            <button 
              className={`tp-btn-card ${isAuthenticated && tournament.register_enabled ? 'tp-btn-primary' : 'tp-btn-secondary'}`}
              disabled={!isAuthenticated || !tournament.register_enabled}
              onClick={() => navigate(getRegistrationPath(tournament.id), { state: { tournament } })}
            >
              {!tournament.register_enabled ? "Registration Closed" : isAuthenticated ? "Join Tournament" : "Login to Join"}
            </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------- MAIN PAGE -------------------- */
export default function TournamentPage() {
  const { tournaments, categories, loading, error } = useTournamentData();
  const navigate = useNavigate(); 
  const isAuthenticated = useSelector(selectIsAuthenticated);

  const [activeFilter, setActiveFilter] = useState("All");
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [selectedTournament, setSelectedTournament] = useState(null); 

  const filterContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const SCROLL_AMOUNT = 220;

  const filteredData = activeFilter === "All"
    ? tournaments
    : tournaments.filter((t) => t.category_name === activeFilter);

  const checkScrollState = useCallback(() => {
    const el = filterContainerRef.current;
    if (!el || !isFilterOpen) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }
    const left = el.scrollLeft;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(left > 8);
    setCanScrollRight(left < Math.max(0, maxScroll - 8));
  }, [isFilterOpen]);

  const scrollFilter = (direction) => {
    const el = filterContainerRef.current;
    if (!el) return;
    const delta = direction === "left" ? -SCROLL_AMOUNT : SCROLL_AMOUNT;
    el.scrollBy({ left: delta, behavior: "smooth" });
    setTimeout(checkScrollState, 300);
  };

  useEffect(() => {
    const el = filterContainerRef.current;
    if (!loading) checkScrollState();
    if (el) {
      const handler = () => checkScrollState();
      el.addEventListener("scroll", handler);
      window.addEventListener("resize", handler);
      return () => {
        el.removeEventListener("scroll", handler);
        window.removeEventListener("resize", handler);
      };
    }
  }, [checkScrollState, loading, categories.length]);

  return (
    <div className="tp-page">
      <header className="tp-header">
        <div className="tp-header-inner">
          <h1 className="tp-title">Upcoming Events & Tournaments</h1>
          <p className="tp-sub">Official tournaments hosted by Aadhav Academy.</p>
          
          <div className="tp-steps-box">
             <div className="tp-step-item"><b>1. New User Register / Existing user login</b></div>
             <div className="tp-step-divider">➔</div>
             <div className="tp-step-item"><b>1. Select Tournament</b></div>
             <div className="tp-step-divider">➔</div>
             <div className="tp-step-item"><b>2. Add Players</b></div>
             <div className="tp-step-divider">➔</div>
             <div className="tp-step-item"><b>3. Secure Payment</b></div>
          </div>
        </div>
      </header>

      <section className="tp-filter">
        <div className="tp-filter-head" onClick={() => setIsFilterOpen((p) => !p)} role="button">
          <span className={`tp-caret ${isFilterOpen ? "open" : ""}`}>▶</span>
          <span className="tp-filter-title">Category</span>
          <span className="tp-filter-current">{activeFilter}</span>
        </div>

        <div className={`tp-filter-wrap ${isFilterOpen ? "open" : "closed"}`}>
          <button className={`tp-arrow tp-arrow-left ${canScrollLeft ? "" : "disabled"}`} onClick={() => scrollFilter("left")} disabled={!canScrollLeft}>‹</button>
          <div ref={filterContainerRef} className="tp-filter-body">
            {categories.map((c) => (
              <button key={c.id} className={`tp-chip ${activeFilter === c.name ? "active" : ""}`} onClick={() => setActiveFilter(c.name)}>{c.name}</button>
            ))}
          </div>
          <button className={`tp-arrow tp-arrow-right ${canScrollRight ? "" : "disabled"}`} onClick={() => scrollFilter("right")} disabled={!canScrollRight}>›</button>
        </div>
      </section>

      {loading && <div className="tp-status">Loading events...</div>}
      {error && <div className="tp-status tp-error">{error}</div>}

      <main className="tp-grid">
        {filteredData.map((t) => (
          <article key={t.id} className="tp-card" onClick={() => setSelectedTournament(t)}>
            <div className="tp-card-media">
              <div className="tp-card-badge">{t.category_name || "Tournament"}</div>
              <img src={t.prospectus_image || defaultImage} alt={t.name} />
            </div>

            <div className="tp-card-body">
              <div className="tp-card-content">
                <h3 className="tp-card-title">{t.name}</h3>
                <div className="tp-meta-stack">
                  <div className="tp-meta-pill"><span>📅</span> {formatDate(t.event_date)}</div>
                  <div className="tp-meta-pill"><span>📍</span> {t.venue || "TBD"}</div>
                </div>
              </div>

              <div className="tp-card-actions">
                <button 
                  className="tp-btn-card tp-btn-secondary" 
                  onClick={(e) => { e.stopPropagation(); setSelectedTournament(t); }}
                >
                  Details
                </button>

                <button
                  className={`tp-btn-card ${isAuthenticated && t.register_enabled ? 'tp-btn-primary' : 'tp-btn-secondary'}`}
                  disabled={!isAuthenticated || !t.register_enabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(getRegistrationPath(t.id), { state: { tournament: t } });
                  }}
                >
                  {!t.register_enabled ? "Closed" : isAuthenticated ? "Join Tournament" : "Login Required"}
                </button>
              </div>
            </div>
          </article>
        ))}
      </main>
      
      <TournamentModal 
        tournament={selectedTournament} 
        onClose={() => setSelectedTournament(null)} 
        navigate={navigate} 
        isAuthenticated={isAuthenticated}
      />

      <style>{`
        :root{
          --tp-bg: #f5f7fb; --tp-surface: #ffffff; --tp-primary: #1e88e5;        
          --tp-text: #0f1724; --tp-subtext: #5b6770; --tp-border: #e6eef8;
          --tp-radius: 16px; --tp-max: 1200px;
        }

        .tp-page { min-height: 100vh; background: var(--tp-bg); padding: 30px 20px; font-family: Inter, sans-serif; color: var(--tp-text); position: relative; }
        
        /* Z-INDEX MANAGEMENT: Keeps header on top of content */
        .tp-header { max-width: var(--tp-max); margin: 0 auto 15px; position: sticky; top: 0; z-index: 100; background: var(--tp-bg); }
        .tp-header-inner { padding-bottom: 20px; border-bottom: 1px solid var(--tp-border); }
        .tp-title { margin: 0; font-weight: 700; font-size: 32px; }
        .tp-sub { margin: 6px 0 0; color: var(--tp-subtext); font-size: 16px; }

        .tp-steps-box { display: flex; align-items: center; gap: 10px; background: #fff; padding: 12px 18px; border-radius: 12px; border: 1px solid var(--tp-border); margin-top: 15px; flex-wrap: wrap; }
        .tp-step-item b { color: var(--tp-primary); font-size: 13px; text-transform: uppercase; letter-spacing: 0.3px; }
        .tp-step-divider { color: #cbd5e1; font-weight: bold; }

        .tp-filter { max-width: var(--tp-max); margin: 15px auto 5px; position: relative; z-index: 90; }
        .tp-filter-head { display:flex; align-items:center; gap: 12px; cursor: pointer; }
        .tp-caret { display:inline-block; transition: transform .22s ease; color: var(--tp-primary); }
        .tp-caret.open { transform: rotate(90deg); }
        .tp-filter-title { font-weight: 600; font-size: 15px; }
        .tp-filter-current { margin-left: auto; color: var(--tp-subtext); font-size: 14px; }
        .tp-filter-wrap { position: relative; margin-top: 10px; transition: all .25s ease; overflow: hidden;}
        .tp-filter-wrap.closed { max-height: 0; opacity: 0; }
        .tp-filter-wrap.open { max-height: 100px; opacity: 1; }
        
        .tp-arrow { position: absolute; top: 50%; transform: translateY(-50%); height: 36px; width: 36px; border-radius: 999px; background: #fff; border: 1px solid var(--tp-border); cursor: pointer; z-index: 6; display: flex; align-items: center; justify-content: center; }
        .tp-arrow.disabled { opacity: 0.3; pointer-events: none; }
        .tp-arrow-left { left: 0; } .tp-arrow-right { right: 0; }
        .tp-filter-body { overflow-x: auto; display: flex; gap: 12px; padding: 10px 45px; scrollbar-width: none; }
        .tp-filter-body::-webkit-scrollbar { display: none; }
        .tp-chip { border: 1px solid var(--tp-border); background: #fff; padding: 8px 16px; border-radius: 999px; font-weight: 600; font-size: 14px; cursor: pointer; white-space: nowrap; transition: 0.2s; }
        .tp-chip.active { background: var(--tp-primary); color: #fff; border-color: var(--tp-primary); }

        .tp-grid { max-width: var(--tp-max); margin: 30px auto; display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 25px; position: relative; z-index: 10; }
        .tp-card { background: #fff; border-radius: 14px; overflow: hidden; display: flex; flex-direction: column; transition: all 0.3s ease; border: 1px solid var(--tp-border); height: 100%; cursor: pointer; }
        .tp-card:hover { transform: translateY(-6px); box-shadow: 0 12px 24px rgba(0,0,0,0.08); border-color: var(--tp-primary); }
        .tp-card-media { position: relative; width: 100%; height: 210px; overflow: hidden; background: #f0f0f0; }
        .tp-card-media img { width: 100%; height: 100%; object-fit: cover; object-position: top; transition: 0.5s; }
        .tp-card-badge { position: absolute; top: 12px; left: 12px; z-index: 2; background: rgba(255,255,255,0.95); padding: 4px 10px; border-radius: 6px; font-size: 10px; font-weight: 800; color: var(--tp-primary); text-transform: uppercase; }
        .tp-card-body { padding: 20px; display: flex; flex-direction: column; flex: 1; }
        .tp-card-title { font-size: 19px; font-weight: 700; color: #1a202c; margin: 0 0 12px; line-height: 1.4; }
        .tp-meta-stack { display: flex; flex-direction: column; gap: 8px; margin-bottom: 20px; }
        .tp-meta-pill { display: flex; align-items: center; gap: 8px; font-size: 13.5px; color: var(--tp-subtext); background: #f8fafc; padding: 6px 10px; border-radius: 8px; }

        .tp-card-actions { display: flex; gap: 10px; margin-top: auto; }
        .tp-btn-card { flex: 1; padding: 11px; border-radius: 10px; font-weight: 700; font-size: 13px; cursor: pointer; border: none; }
        .tp-btn-secondary { background: #f1f5f9; color: #475569; }
        .tp-btn-primary { background: var(--tp-primary); color: #fff; }
        .tp-btn-card:disabled { opacity: 0.6; cursor: not-allowed; }

        .tp-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); display: flex; align-items: center; justify-content: center; z-index: 9999; backdrop-filter: blur(2px); padding: 20px; }
        .tp-modal-content { background: #fff; width: 100%; max-width: 550px; border-radius: 16px; max-height: 90vh; overflow: hidden; display: flex; flex-direction: column; position: relative; }
        .tp-modal-close-btn { position: absolute; top: 10px; right: 15px; font-size: 28px; background: none; border: none; cursor: pointer; color: #94a3b8; }
        .tp-modal-header-tag { font-size: 11px; color: var(--tp-primary); font-weight: 700; text-transform: uppercase; padding: 20px 20px 0; }
        .tp-modal-name { font-size: 22px; font-weight: 700; padding: 5px 20px 15px; border-bottom: 1px solid var(--tp-border); }
        .tp-modal-scroll-area { padding: 20px; overflow-y: auto; }
        .tp-modal-media img { width: 100%; border-radius: 10px; }
        .tp-modal-actions { padding: 15px 20px; display: flex; gap: 10px; border-top: 1px solid var(--tp-border); }

        @media (max-width: 600px) {
          .tp-steps-box { flex-direction: column; align-items: flex-start; }
          .tp-step-divider { display: none; }
          .tp-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
}